
import React, { useState, useRef, useCallback } from 'react';
import { DisplayedImage, FilterType, StarType, CustomFolder } from '../types';
import LoadingSpinner from './LoadingSpinner';
import InfoIcon from './InfoIcon';
import CloseIcon from './CloseIcon';
import ImageActionToolbar, { ImageActionType } from './ImageActionToolbar';
import ImageQuickActionToolbar, { ImageQuickActionType } from './ImageQuickActionToolbar';
import StarPicker, { starIconsMap } from './StarPicker';
import StarIconEmpty from './icons/stars/StarIconEmpty';
import { formatImageSuffix } from '../App'; 
import SaveDestinationModal from './SaveDestinationModal';
import { USER_CREATED_WORK_SPANISH_NAME, USER_EXPORTED_ART_SPANISH_NAME } from '../constants';

interface ImageViewerProps {
  image: DisplayedImage | null;
  isProcessing: boolean; 
  isSavingToDrafts: boolean; 
  isSavingToFinalArtwork: boolean; 
  isSavingToCustomFolderId: string | null;
  onConfirmSaveToDrafts: () => void;
  onConfirmSaveToFinalArtwork: () => void;
  onConfirmSaveToCustomFolder: (customFolder: CustomFolder) => void;
  customFolders: CustomFolder[];
  onAddCustomFolder: (folderName: string) => Promise<boolean>;
  onApplyFilter: (type: FilterType, value: string, displayName: string) => void;
  onNavigateToStore: () => void;
  onNavigateToImageLocation: (path: string) => void; 
  currentStar?: StarType;
  onSetStar: (star: StarType) => void;
  showSaveOptionsButton: boolean; 
  onImageDirtied: (actionLabel: string) => void;
  onDebugHover?: (name: string | null) => void; 
  setIsSaveModalOpen: (isOpen: boolean) => void;
}

interface FormattedImageData {
  overlayCategoryTitle: string | null;
  detailedInfoNode: React.ReactNode | null;
  rawFilterData?: {
    date: string; 
    artist: string;
    machineCode: string; 
  }
}

const FILENAME_PARSE_PATTERN_VIEWER = /^(.+?)_(\d{4}-\d{2}-\d{2}) (\d{2}-\d{2}-\d{2})_(\d{5})-(\d+)-(.+?)-(.+?)\.(?:jpg|jpeg|png)$/i;

const formatImageData = (
    fullOriginalFilename: string, 
    imageDisplayName: string | undefined, 
    onApplyFilter: (type: FilterType, value: string, displayName: string) => void
  ): FormattedImageData => {
  const match = fullOriginalFilename.match(FILENAME_PARSE_PATTERN_VIEWER);

  const clickableClass = "cursor-pointer hover:text-yellow-400 hover:underline transition-colors";
  let titleForOverlayDisplay: string | null = imageDisplayName || null;
  if (!titleForOverlayDisplay && match) {
    titleForOverlayDisplay = match[1]; 
  }
  if (!titleForOverlayDisplay) { 
    titleForOverlayDisplay = fullOriginalFilename.length > 50 ? fullOriginalFilename.substring(0, 47) + "..." : fullOriginalFilename;
  }

  if (match) {
    const [, category, dateStr, timeStr, serial, seed, artist, machineCodeWithPrefix] = match;
    if (!imageDisplayName) {
        titleForOverlayDisplay = category;
    }
    const [year, month, day] = dateStr.split('-');
    const formattedDisplayDate = `${day}/${month}/${year}`; 
    const rawDateForFilter = dateStr; 
    const formattedTime = timeStr.replace(/-/g, ':');
    const machineCode = machineCodeWithPrefix.startsWith("___")
      ? machineCodeWithPrefix.substring(3)
      : machineCodeWithPrefix;

    const detailsNode = (
      <>
        <span className="block text-xs text-gray-300 mb-0.5 leading-tight">
          <button
            onClick={() => onApplyFilter('date', rawDateForFilter, `Fecha: ${formattedDisplayDate}`)}
            className={clickableClass}
            title={`Ver todo de ${formattedDisplayDate}`}
          >
            {formattedDisplayDate}
          </button>
          {` - ${formattedTime}`}
        </span>
        <span className="block text-xxs text-gray-400 mb-0.5 leading-tight">
          <span className="font-semibold text-gray-100">Serie:</span> {serial} &bull; <span className="font-semibold text-gray-100">Semilla:</span> {seed}
        </span>
        <span className="block text-[9px] text-gray-400 leading-tight">
          <span className="font-semibold text-gray-100">Por:</span>{' '}
          <button
            onClick={() => onApplyFilter('artist', artist, `Artista: ${artist}`)}
            className={clickableClass}
            title={`Ver todo de ${artist}`}
          >
            {artist}
          </button>
          {' '} &bull; <span className="font-semibold text-gray-100">Generadora:</span>{' '}
          <button
            onClick={() => onApplyFilter('machineCode', machineCode, `Máquina: ${machineCode}`)}
            className={clickableClass}
            title={`Ver todo de la máquina ${machineCode}`}
          >
            {machineCode}
          </button>
        </span>
      </>
    );
    return {
        overlayCategoryTitle: titleForOverlayDisplay,
        detailedInfoNode: detailsNode,
        rawFilterData: { date: rawDateForFilter, artist, machineCode }
    };
  }
  return { overlayCategoryTitle: titleForOverlayDisplay, detailedInfoNode: null };
};

const ImageViewer: React.FC<ImageViewerProps> = ({
  image,
  isProcessing,
  isSavingToDrafts,
  isSavingToFinalArtwork,
  isSavingToCustomFolderId,
  onConfirmSaveToDrafts,
  onConfirmSaveToFinalArtwork,
  onConfirmSaveToCustomFolder,
  customFolders,
  onAddCustomFolder,
  onApplyFilter,
  onNavigateToStore,
  onNavigateToImageLocation,
  currentStar = StarType.None,
  onSetStar,
  showSaveOptionsButton,
  onImageDirtied,
  onDebugHover, 
  setIsSaveModalOpen,
}) => {
  const [showInfotextModal, setShowInfotextModal] = useState<boolean>(false);
  const [isStarPickerOpen, setIsStarPickerOpen] = useState<boolean>(false);
  const [showSaveDestinationModal, setShowSaveDestinationModal] = useState<boolean>(false);
  const starButtonRef = useRef<HTMLButtonElement>(null);

  if (!image) {
    return null;
  }

  const anySaveInProgress = isSavingToDrafts || isSavingToFinalArtwork || !!isSavingToCustomFolderId;
  const grabarButtonDisabled = isProcessing || !image.url || anySaveInProgress;
  const storeButtonDisabled = isProcessing || !image.url || anySaveInProgress;

  const toggleInfotextModal = () => {
    if (image.rawDescription) {
      setShowInfotextModal(!showInfotextModal);
    }
  };

  const handleOpenSaveModal = () => {
    if (image) {
      setIsSaveModalOpen(true);
      setShowSaveDestinationModal(true);
    }
  };

  const handleCloseSaveModal = useCallback(() => {
    setShowSaveDestinationModal(false);
    setIsSaveModalOpen(false);
  }, [setIsSaveModalOpen]);

  const handleImageAction = (actionType: ImageActionType, actionLabel: string) => {
    console.log(`Action triggered: ${actionType} for image ${image?.id}. Label: ${actionLabel}`);
    if (image) {
        onImageDirtied(actionLabel);
    }
    alert(`Funcionalidad "${actionLabel}" próximamente.`);
  };

  const handleImageQuickAction = (actionType: ImageQuickActionType, actionLabel: string) => {
    console.log(`Quick Action triggered: ${actionType} for image ${image?.id}. Label: ${actionLabel}`);
    if (image) {
        onImageDirtied(actionLabel);
    }
    alert(`Funcionalidad "${actionLabel}" próximamente.`);
  };

  const formattedData = formatImageData(image.alt, image.displayName, onApplyFilter);
  const suffixForAlt = formatImageSuffix(image.parsedDate, image.parsedTime, image.parsedMachineCode) || "";
  const altText = `${image.displayName || image.alt}${suffixForAlt}`;

  const categoryTitleDivTooltip = image.displayName 
    ? `${image.displayName}${suffixForAlt}` 
    : image.alt; 

  const ActiveStarIcon = starIconsMap[currentStar]?.icon || StarIconEmpty;
  const activeStarColor = starIconsMap[currentStar]?.color.split(' ')[0] || 'text-gray-400'; 

  const handleStarButtonClick = () => {
    setIsStarPickerOpen(prev => !prev);
  };

  const handleSelectStar = (star: StarType) => {
    onSetStar(star);
    setIsStarPickerOpen(false);
  };

  let statusBadgeText = null;
  let statusBadgeColor = '';
  let statusBadgeTitle = '';

  if (image.isExported) {
    statusBadgeText = USER_EXPORTED_ART_SPANISH_NAME;
    statusBadgeColor = "bg-purple-600 hover:bg-purple-500"; 
    statusBadgeTitle = `Abrir ubicación: ${USER_EXPORTED_ART_SPANISH_NAME}`;
  } else if (image.savedToCustomFolderPath) {
      const pathParts = image.savedToCustomFolderPath.split('/').filter(Boolean);
      const customFolderName = pathParts.length > 2 ? pathParts[pathParts.length-1] : "Carpeta Personalizada";
      statusBadgeText = `En: ${customFolderName}`;
      const folder = customFolders.find(cf => image.savedToCustomFolderPath?.includes(`/${cf.name}/`));
      statusBadgeColor = folder?.color ? `${folder.color} ${folder.color.replace('-600', '-500').replace('-500','-400')}` : 'bg-gray-600 hover:bg-gray-500';
      statusBadgeTitle = `Abrir carpeta: ${customFolderName}`;
  } else if (image.isSaved) {
      statusBadgeText = USER_CREATED_WORK_SPANISH_NAME;
      statusBadgeColor = "bg-green-600 hover:bg-green-500";
      statusBadgeTitle = `Abrir ubicación: ${USER_CREATED_WORK_SPANISH_NAME}`;
  }

  const handleStatusBadgeClick = () => { 
    if (image?.s3Prefix) {
      onNavigateToImageLocation(image.s3Prefix);
    }
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-start p-2">
      <div className="w-full relative flex justify-center items-center mb-2 px-2 h-12"> {/* mb-2 for title */}
        {formattedData.overlayCategoryTitle && (
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 inline-block max-w-lg text-center px-4 py-2 bg-indigo-700 text-white rounded-lg shadow-xl ring-1 ring-indigo-500 text-xl font-bold"
            title={categoryTitleDivTooltip} 
            onMouseEnter={() => onDebugHover?.(`ImageViewer: Title Overlay: ${formattedData.overlayCategoryTitle}`)}
            onMouseLeave={() => onDebugHover?.(null)}
          >
            {formattedData.overlayCategoryTitle}
          </div>
        )}
      </div>
      
      <div 
        className="w-full flex-shrink-0 mb-2 flex items-center justify-center" 
        style={{maxHeight: 'calc(50vh - 2rem)'}}
        onMouseEnter={() => onDebugHover?.("ImageViewer: Main Image Area Container")}
        onMouseLeave={() => onDebugHover?.(null)}
      > 
        {/* This div is the relative context for all absolute positioned elements */}
        <div className="relative inline-block max-w-full max-h-full">
            <img
                key={image.id}
                src={image.url}
                alt={altText} 
                className="max-w-full max-h-full object-contain rounded-md shadow-2xl block"
            />
            
            {/* TIENDA and Star Button Group - Top Right */}
            <div className="absolute top-0 right-0 z-40 flex flex-col items-end space-y-1 p-0.5">
            {/*
              <button
                onClick={onNavigateToStore}
                onMouseEnter={() => onDebugHover?.("ImageViewer: Store Button")}
                onMouseLeave={() => onDebugHover?.(null)}
                disabled={storeButtonDisabled}
                className="px-3 py-1 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-md shadow-lg transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center text-xs"
                title="Ahora traete estas imagenes al plano real en nuestra tienda"
              >
                TIENDA
              </button>
            */}

              <button
                ref={starButtonRef}
                onClick={handleStarButtonClick}
                onMouseEnter={() => onDebugHover?.("ImageViewer: Star Button")}
                onMouseLeave={() => onDebugHover?.(null)}
                className={`p-1.5 bg-black bg-opacity-30 hover:bg-opacity-50 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-opacity-75 shadow-lg ${activeStarColor}`}
                aria-label={isStarPickerOpen ? "Cerrar selector de estrellas" : "Abrir selector de estrellas"}
                aria-haspopup="true"
                aria-expanded={isStarPickerOpen}
                id="star-picker-button"
                title={starIconsMap[currentStar]?.label || "Marcar imagen"}
              >
                <ActiveStarIcon className="w-5 h-5" />
              </button>
            </div>

            {isStarPickerOpen && (
                <StarPicker
                  currentStar={currentStar}
                  onSelectStar={handleSelectStar}
                  onClose={() => setIsStarPickerOpen(false)}
                  triggerRef={starButtonRef}
                />
            )}

            {/* Info Button - Top Left */}
            {image.rawDescription && (
              <div className="absolute top-0 left-0 z-20 p-0.5"> 
                <button
                  onClick={toggleInfotextModal}
                  onMouseEnter={() => onDebugHover?.("ImageViewer: Info Button")}
                  onMouseLeave={() => onDebugHover?.(null)}
                  className="p-1.5 bg-black bg-opacity-40 hover:bg-opacity-60 rounded-full text-white transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-opacity-75 shadow-lg"
                  aria-label={showInfotextModal ? "Ocultar información detallada de la imagen" : "Mostrar información detallada de la imagen"}
                  aria-expanded={showInfotextModal}
                  title={showInfotextModal ? "Ocultar información" : "Mostrar información"}
                >
                  <InfoIcon className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Left Toolbar */}
            <div className="absolute top-1/2 -translate-y-1/2 left-0 z-20 pl-0.5">
              <ImageActionToolbar
                onAction={handleImageAction}
                disabled={isProcessing || anySaveInProgress}
                onDebugHover={onDebugHover} 
              />
            </div>

            {/* Right Toolbar */}
            <div className="absolute top-1/2 -translate-y-1/2 right-0 z-20 pr-0.5">
              <ImageQuickActionToolbar
                onQuickAction={handleImageQuickAction}
                disabled={isProcessing || anySaveInProgress}
                onDebugHover={onDebugHover}
              />
            </div>
                
            {/* Grabar / Grabado En Button - Bottom Right */}
            {showSaveOptionsButton && (
              <button
                onClick={handleOpenSaveModal}
                onMouseEnter={() => onDebugHover?.("ImageViewer: Grabar Button (opens modal)")}
                onMouseLeave={() => onDebugHover?.(null)}
                disabled={grabarButtonDisabled}
                className="absolute bottom-0 right-0 z-20 m-0.5 px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-md shadow-lg transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center text-xs"
                title="Grabar esta imagen"
              >
                {anySaveInProgress ? (
                  <><LoadingSpinner size="sm" /> <span className="ml-1.5">Guardando...</span></>
                ) : (
                  'Grabar'
                )}
              </button>
            )}

            {!showSaveOptionsButton && statusBadgeText && image.s3Prefix && (
              <button
                onClick={handleStatusBadgeClick}
                onMouseEnter={() => onDebugHover?.(`ImageViewer: 'Grabado en: ${statusBadgeText.replace("En: ", "")}' Button`)}
                onMouseLeave={() => onDebugHover?.(null)}
                title={statusBadgeTitle}
                className={`absolute bottom-0 right-0 z-20 m-0.5 px-3 py-1 text-white font-semibold rounded-md shadow-lg transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-75 flex items-center justify-center text-xs ${statusBadgeColor} disabled:opacity-60 disabled:cursor-not-allowed`}
                disabled={!image.s3Prefix}
                aria-label={statusBadgeTitle}
              >
                Grabado en: {statusBadgeText.replace("En: ", "")}
              </button>
            )}
            {!showSaveOptionsButton && statusBadgeText && !image.s3Prefix && (
                <span 
                    onMouseEnter={() => onDebugHover?.(`ImageViewer: 'Grabado en: ${statusBadgeText.replace("En: ", "")}' (no link)`)}
                    onMouseLeave={() => onDebugHover?.(null)}
                    className={`absolute bottom-0 right-0 z-20 m-0.5 px-3 py-1 text-white font-semibold rounded-md shadow-lg flex items-center justify-center text-xs ${statusBadgeColor.split(' ')[0]}`}>
                    Grabado en: {statusBadgeText.replace("En: ", "")}
                </span>
            )}
        </div> {/* End of relative inline-block wrapper for image and absolute elements */}
      </div>


      <div
        className="w-full max-w-2xl max-h-24 overflow-y-auto scrollbar-thin scrollbar-thumb-yellow-500 scrollbar-track-gray-700 bg-gray-800 border border-yellow-600 p-4 rounded-lg shadow-md text-left space-y-3"
      >
        {formattedData.detailedInfoNode && (
          <div
            className="text-center"
            title={image.alt} 
          >
            {formattedData.detailedInfoNode}
          </div>
        )}

        <div className="space-y-1">
            {image.appliedEdit && (
            <p className="text-xs text-sky-300" title={`Edición aplicada: ${image.appliedEdit}`}>
                Edición: <span className="font-semibold">{image.appliedEdit}</span>
            </p>
            )}

            {image.isGenerated && image.promptUsed && (
            <p className={`text-xs ${image.appliedEdit ? 'text-gray-400' : 'text-indigo-300'}`} title={`Prompt: ${image.promptUsed}`}>
                Prompt: "{image.promptUsed.substring(0,150)}{image.promptUsed.length > 150 ? '...' : ''}"
            </p>
            )}

            {!image.isGenerated && image.description && !image.rawDescription && (
            <p className="text-sm text-gray-300" title={image.description}>
                {image.description}
            </p>
            )}

            {image.analysisResult && (
            <div className="mt-1">
                <h4 className="text-xs font-semibold text-green-300 mb-0.5">Análisis Gemini:</h4>
                <p className="text-sm text-green-200 italic whitespace-pre-wrap">
                {image.analysisResult}
                </p>
            </div>
            )}
        </div>
      </div>

      {showInfotextModal && image.rawDescription && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-75"
          role="dialog"
          aria-modal="true"
          aria-labelledby="infotext-modal-title"
          onClick={toggleInfotextModal}
        >
          <div
            className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-xl w-full max-h-[80vh] flex flex-col text-left relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 id="infotext-modal-title" className="text-xl font-semibold text-indigo-300">Información Detallada</h2>
              <button
                onClick={toggleInfotextModal}
                onMouseEnter={() => onDebugHover?.("Modal: Close Infotext")}
                onMouseLeave={() => onDebugHover?.(null)}
                className="p-1 text-gray-400 hover:text-gray-100 rounded-full hover:bg-gray-700 transition-colors"
                aria-label="Cerrar ventana de información"
              >
                <CloseIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-700 flex-grow">
              <pre className="text-sm text-gray-200 whitespace-pre-wrap break-all">
                {image.rawDescription}
              </pre>
            </div>
          </div>
        </div>
      )}
      
      {image && (
        <SaveDestinationModal
            isOpen={showSaveDestinationModal}
            onClose={handleCloseSaveModal}
            image={image}
            onSaveToDrafts={() => {
                onConfirmSaveToDrafts();
            }}
            onSaveToFinalArtwork={() => {
                onConfirmSaveToFinalArtwork();
            }}
            onSaveToCustomFolder={(folder) => {
                onConfirmSaveToCustomFolder(folder);
            }}
            customFolders={customFolders}
            onAddFolder={onAddCustomFolder}
            isSavingToDrafts={isSavingToDrafts}
            isSavingToFinalArtwork={isSavingToFinalArtwork}
            isSavingToCustomFolderId={isSavingToCustomFolderId}
            onDebugHover={onDebugHover}
        />
      )}
    </div>
  );
};

export default ImageViewer;
